import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Pause, Play, Volume2, VolumeX } from "lucide-react";
import { MaskLines, Reveal, SectionLabel } from "./Motion";
import { useT } from "@/i18n";

const CtrlBtn = ({ onClick, label, children, id }) => (
  <button onClick={onClick} aria-label={label} className="grid h-12 w-12 place-items-center rounded-full border border-white/20 bg-[#121110]/70 text-[#F7F5F0] backdrop-blur transition-[background-color,color] duration-300 hover:bg-[#D98A52] hover:text-[#121110]" data-testid={id}>
    {children}
  </button>
);

export const VideoReel = () => {
  const t = useT();
  const ref = useRef(null);
  const video = useRef(null);
  const [playing, setPlaying] = useState(true);
  const [muted, setMuted] = useState(true);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "center center"] });
  const clip = useTransform(scrollYProgress, [0, 1], ["inset(14% 14% 14% 14% round 24px)", "inset(0% 0% 0% 0% round 0px)"]);

  useEffect(() => {
    const el = video.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && playing) el.play().catch(() => {});
      else el.pause();
    }, { threshold: 0.2 });
    io.observe(el);
    return () => io.disconnect();
  }, [playing]);

  const togglePlay = () => {
    const el = video.current;
    if (!el) return;
    if (el.paused) { el.play().catch(() => {}); setPlaying(true); } else { el.pause(); setPlaying(false); }
  };
  const toggleMute = () => {
    const el = video.current;
    if (!el) return;
    el.muted = !el.muted;
    setMuted(el.muted);
  };

  return (
    <section id="video" ref={ref} className="grain relative bg-[#121110] px-5 py-28 text-[#F7F5F0] sm:px-8 lg:py-40" data-testid="video-section">
      <div className="mx-auto grid max-w-[1500px] gap-14 lg:grid-cols-12 lg:items-center">
        <div className="lg:col-span-5">
          <SectionLabel index="▶" dark>{t("video.label", "Dentro lo stabilimento")}</SectionLabel>
          <MaskLines
            className="mt-10 font-display text-4xl font-bold leading-[1.02] tracking-tight sm:text-5xl lg:text-[4.2rem]"
            lines={[t("video.h1", "Affidati a"), <span key="v"><em className="font-serif font-normal text-[#D98A52]">Emma</em> Industrie.</span>]}
          />
          <Reveal delay={0.2} className="mt-8 max-w-md text-base leading-relaxed text-[#A39E93] sm:text-lg">
            {t("video.text", "Dalla lavorazione delle lamelle alle grandi coperture finite: guarda come nascono le nostre strutture in legno lamellare e XLAM.")}
          </Reveal>
        </div>
        <motion.div style={{ clipPath: clip }} className="relative aspect-square w-full overflow-hidden bg-[#1B1816] lg:col-span-7">
          <video
            ref={video}
            src="/video/produzione.mp4"
            poster="/video/produzione-poster.jpg"
            muted
            loop
            playsInline
            autoPlay
            preload="metadata"
            className="h-full w-full object-cover"
            data-testid="production-video"
          />
          <div className="absolute bottom-5 left-5 flex gap-2">
            <CtrlBtn onClick={togglePlay} label={playing ? t("video.pause", "Pausa") : t("video.play", "Riproduci")} id="video-play-toggle">
              {playing ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
            </CtrlBtn>
            <CtrlBtn onClick={toggleMute} label={muted ? t("video.unmute", "Attiva audio") : t("video.mute", "Disattiva audio")} id="video-mute-toggle">
              {muted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
            </CtrlBtn>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
